/**
 * Generic hook for loading items in batches with intersection observer
 *
 * @remarks
 * This is a reusable hook that implements progressive/lazy loading for any type of content.
 * It uses Intersection Observer to detect when to load more items and includes debouncing
 * to prevent rapid consecutive loads.
 *
 * Features:
 * - Automatic loading when trigger element comes into view
 * - Configurable threshold distance for early loading
 * - Built-in rate limiting (500ms minimum between loads)
 * - Support for custom scroll containers
 * - Reset functionality via resetKey prop
 *
 * @example
 * Basic usage
 * ```typescript
 * const { items, loading, error, hasMore, loadTriggerRef } = useBatchedLoader({
 *   fetchBatch: async (startIndex, batchSize) => {
 *     const response = await api.getItems(startIndex, batchSize);
 *     return {
 *       items: response.data,
 *       hasMore: response.page < response.totalPages
 *     };
 *   },
 *   batchSize: 10,
 *   loadThreshold: 200,
 * });
 * ```
 *
 * @example
 * With custom scroll container and reset
 * ```typescript
 * const scrollRef = useRef<HTMLDivElement>(null);
 * const { items, loadTriggerRef, reset } = useBatchedLoader({
 *   fetchBatch: fetchItems,
 *   batchSize: 20,
 *   initialBatchSize: 30,
 *   loadThreshold: 400,
 *   scrollContainerRef: scrollRef,
 *   resetKey: searchQuery, // Resets when searchQuery changes
 * });
 * ```
 */

import { useState, useEffect, useRef, useCallback } from "react";

import { UseBatchedLoaderOptions, UseBatchedLoaderResult } from "./types";

const useBatchedLoader = <TItem>({
  fetchBatch,
  batchSize = 10,
  initialBatchSize,
  loadThreshold = 200,
  resetKey,
  scrollContainerRef,
}: UseBatchedLoaderOptions<TItem>): UseBatchedLoaderResult<TItem> => {
  const [items, setItems] = useState<TItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const observerRef = useRef<IntersectionObserver | null>(null);
  const isLoadingRef = useRef(false);
  const lastLoadTimeRef = useRef<number>(0);
  const loadMoreRef = useRef<(() => Promise<void>) | null>(null);

  const MIN_LOAD_INTERVAL = 500; /** Minimum 500ms between loads */

  /** Reset function */
  const reset = useCallback(() => {
    setItems([]);
    setCurrentIndex(0);
    setHasMore(true);
    setError(null);
    setLoading(true);
  }, []);

  /** Reset when resetKey changes */
  useEffect(() => {
    if (resetKey !== undefined) {
      reset();
    }
  }, [resetKey, reset]);

  /** Initial fetch */
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        setError(null);

        const result = await fetchBatch(0, initialBatchSize || batchSize);

        setItems(result.items);
        setCurrentIndex(result.items.length);
        setHasMore(result.hasMore);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load items");
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [fetchBatch, batchSize, initialBatchSize]);

  /** Load more items */
  const loadMore = useCallback(async () => {
    const now = performance.now();
    const timeSinceLastLoad = now - lastLoadTimeRef.current;

    if (isLoadingRef.current || !hasMore) {
      return;
    }

    // Prevent rapid consecutive loads
    if (timeSinceLastLoad < MIN_LOAD_INTERVAL) {
      return;
    }

    isLoadingRef.current = true;
    lastLoadTimeRef.current = now;

    try {
      const result = await fetchBatch(currentIndex, batchSize);

      setItems((prev) => [...prev, ...result.items]);
      setCurrentIndex((prev) => prev + result.items.length);
      setHasMore(result.hasMore);

      /** After loading, check if we should load more immediately */
      /** This handles fast scrolling where the trigger element may already be visible */
      if (result.hasMore) {
        /** Wait a tick for the DOM to update, then check if we need more content */
        setTimeout(() => {
          if (loadMoreRef.current) {
            loadMoreRef.current();
          }
        }, 100);
      }
    } catch (err) {
      console.error("Failed to load more items:", err);
      setError(
        err instanceof Error ? err.message : "Failed to load more items",
      );
    } finally {
      isLoadingRef.current = false;
    }
  }, [fetchBatch, currentIndex, batchSize, hasMore]);

  /** Keep loadMoreRef updated */
  useEffect(() => {
    loadMoreRef.current = loadMore;
  }, [loadMore]);

  /** Intersection observer callback */
  const loadTriggerRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (loading) {
        return;
      }

      /** Disconnect previous observer */
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      /** Create new observer */
      if (node && hasMore) {
        const scrollContainer = scrollContainerRef?.current;
        observerRef.current = new IntersectionObserver(
          (entries) => {
            const isIntersecting = entries[0].isIntersecting;
            if (
              isIntersecting &&
              !isLoadingRef.current &&
              loadMoreRef.current
            ) {
              loadMoreRef.current();
            }
          },
          {
            /** Use custom scroll container if provided */
            root: scrollContainer || null,
            /** Load content before the trigger comes into view */
            rootMargin: `${loadThreshold}px`,
            /** Fire as soon as any part intersects */
            threshold: 0,
          },
        );

        observerRef.current.observe(node);
      }
    },
    [loading, hasMore, loadThreshold, scrollContainerRef],
  );

  /** Cleanup observer on unmount */
  useEffect(
    () => () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    },
    [],
  );

  return {
    items,
    loading,
    error,
    hasMore,
    loadTriggerRef,
    loadMore,
    reset,
  };
};

export { useBatchedLoader };
export type {
  BatchResult,
  UseBatchedLoaderOptions,
  UseBatchedLoaderResult,
} from "./types";
